import {
  Badge,
  Button,
  Dropdown,
  Icons,
  InputErrorMessage,
  NumberInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import { hasZeroPercentOwner } from './compute';
import {
  INCOMPLETE_ENTITY_DETAILS_ERROR,
  MIN_PARTNERS,
  MIN_PARTNERS_ERROR,
  hasIncompleteEntityDetails,
  zeroPercentOwnerError,
} from './copy';
import { type OwnershipEntry, ownerDisplayName } from './types';

interface PartnershipTableProps {
  ids: string[];
  entries: Record<string, OwnershipEntry>;
  // Σ of the entered percentages, and whether it equals 100 (P3).
  percentTotal: number;
  percentBalanced: boolean;
  // True when exactly one partner is the GP (drives the inline P1 error).
  hasGeneralPartner: boolean;
  // False for a Limited-Partners-only structure — hides the GP copy + P1 error.
  gpApplies: boolean;
  onAddOwner: () => void;
  onEditOwner: (id: string) => void;
  onRemoveOwner: (id: string) => void;
  // Whether entity owners carry a details file; drives the incomplete badge.
  collectsEntityDetails?: boolean;
  onUpdatePercent: (id: string, percent: number | undefined) => void;
}

const PercentSuffix = () => (
  <span className="flex w-full items-center justify-center txt-small-regular text-text-tertiary">%</span>
);

export const PartnershipTable = ({
  ids,
  entries,
  percentTotal,
  percentBalanced,
  hasGeneralPartner,
  gpApplies,
  onAddOwner,
  onEditOwner,
  onRemoveOwner,
  collectsEntityDetails,
  onUpdatePercent,
}: PartnershipTableProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium text-text-primary">Partners</h3>
          <p className="text-sm text-text-secondary">
            Add partners and define their ownership percentage.
            {gpApplies
              && ' Exactly one partner must be designated General Partner.'}
          </p>
        </div>
        <Button variant="secondary" size="sm" type="button" onClick={onAddOwner}>
          + Add partner
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Partner</TableHead>
            <TableHead className="w-[240px]">% owned</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {ids.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="py-6 text-center text-sm text-text-tertiary">
                No partners added yet.
              </TableCell>
            </TableRow>
          ) : (
            ids.map((id) => {
              const entry = entries[id];
              if (!entry) return null;
              const needsDetails = !!collectsEntityDetails && entry.owner.kind === 'entity';
              return (
                <TableRow key={id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{ownerDisplayName(entry.owner)}</span>
                      <Badge variant="gray" size="sm">
                        {entry.owner.kind === 'individual' ? 'Individual' : 'Entity'}
                      </Badge>
                      {needsDetails && !entry.details ? (
                        <button type="button" onClick={() => onEditOwner(id)} className="cursor-pointer">
                          <Badge variant="warning" size="sm">
                            Details needed
                          </Badge>
                        </button>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <NumberInput
                      formatWithCommas
                      className="pr-9 text-right"
                      placeholder="0.00"
                      value={entry.ownershipPercent ?? undefined}
                      rightEl={PercentSuffix}
                      onChange={(n) => onUpdatePercent(id, n ?? undefined)}
                    />
                  </TableCell>
                  <TableCell>
                    <Dropdown
                      menu={{
                        groups: [
                          {
                            items: [
                              { label: 'Edit', onClick: () => onEditOwner(id) },
                              { label: 'Remove', onClick: () => onRemoveOwner(id) },
                            ],
                          },
                        ],
                      }}
                      align="end"
                    >
                      <div className="group flex size-8 cursor-pointer items-center justify-center">
                        <Icons.DotsVertical className="size-4 text-fg-quaternary group-hover:text-fg-quaternary-hover" />
                      </div>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              );
            })
          )}
          <TableRow>
            <TableCell className="txt-small-medium text-text-primary">Total</TableCell>
            <TableCell>
              <NumberInput
                formatWithCommas
                className="pr-9 text-right"
                value={percentTotal}
                rightEl={PercentSuffix}
                disabled
                onChange={() => undefined}
              />
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>

      {ids.length > 0 && !percentBalanced ? <InputErrorMessage message="Ownership must total 100%." /> : null}
      {ids.length > 0 && ids.length < MIN_PARTNERS ? <InputErrorMessage message={MIN_PARTNERS_ERROR} /> : null}
      {percentBalanced && hasZeroPercentOwner(ids, entries) ? (
        <InputErrorMessage message={zeroPercentOwnerError('partnership')} />
      ) : null}
      {hasIncompleteEntityDetails(ids, entries, !!collectsEntityDetails) ? (
        <InputErrorMessage message={INCOMPLETE_ENTITY_DETAILS_ERROR} />
      ) : null}
      {gpApplies && ids.length > 0 && !hasGeneralPartner ? (
        <InputErrorMessage message="Exactly one partner must be designated General Partner." />
      ) : null}
    </div>
  );
};
